import { ChangeEvent, useState, MouseEvent as ReactMouseEvent } from "react"

import { ApiError, ErrorsMap } from "api/http/utils"
import { Box, rgbToHex } from "@material-ui/core"
import ConfirmationModal, {
  ModalInfo,
} from "components/Modals/ConfirmationModal"
import { MediaConfigScope, MediaType } from "./types"
import {
  createMediaType,
  deleteMediaType,
  fetchMediaTypeSemantics,
  fetchMediaTypes,
  patchMediaType,
} from "api/globalMediaTypes"
import { css, cx } from "@emotion/css"
import { queryCache, useMutation, useQuery } from "react-query"

import Breadcrumbs from "components/Breadcrumbs"
import Button from "ui/buttons/Button"
import { ButtonGroup } from "components/buttons/ButtonGroup"
import { ColorPicker } from "./ColorPicker"
import { EditMediaTypeDrawer } from "./EditDrawer"
import { EmptyState } from "components/EmptyState"
import { Header } from "components/Page"
import { Loading } from "components/Loading"
import { ReactComponent as MediaTypeIcon } from "assets/icon-media-type.svg"
import { MediaTypesList } from "../components/MediaTypesList"
import Options from "components/inputs/Options"
import Surface from "components/Surface"
import { TextInput } from "components/inputs/TextInput"
import { WithAuthority } from "components/WithAuthority"
import { colorOptions } from "utils/color"
import { useAuthenticated } from "providers/A12n"
import { useI18n } from "providers/I18n"
import { SelectAgency } from "../components/SelectAgency"

const InitialState: MediaType = {
  id: "",
  name: "",
  semanticCode: "",
  color: colorOptions[0],
  status: "INCOMPLETE",
  configOwnerId: "",
  configScope: "GLOBAL",
  mediaFields: [],
  submediaTypes: [],
}

type Props = {
  configScope?: MediaConfigScope
  configOwnerId?: string
}

const GlobalMediaTypes = (props: Props) => {
  const { t } = useI18n()
  const { configScope = "GLOBAL", configOwnerId } = props
  const [state, setState] = useState<MediaType>(InitialState)
  const [open, setOpen] = useState<{ isOpen: boolean; id?: number }>(() => ({
    isOpen: false,
  }))
  const [modalInfo, setModalInfo] = useState<ModalInfo>()
  const [errors, setErrors] = useState<ErrorsMap | undefined>()

  // ** fetch media types list
  const confScope = configScope === "MARKET_AGENCY" ? "AGENCY" : configScope
  const enabled =
    configScope === "GLOBAL" ? !!configScope : !!configScope && !!configOwnerId
  const { data, status, updatedAt, isLoading } = useQuery(
    ["global-media-types", { configScope: confScope, configOwnerId }],
    fetchMediaTypes,
    {
      enabled,
      onSuccess: (d) => {
        const hasIdxChanged =
          open?.id !== undefined && d[open?.id].id !== state.id
        const newIdx = d?.findIndex((_d) => _d.id === state?.id)
        hasIdxChanged && setOpen({ isOpen: true, id: newIdx })
        setState((s) =>
          open?.id !== undefined && !hasIdxChanged ? d[open?.id] : s,
        )
      },
    },
  )
  const loading = isLoading || status === "idle"

  // ** fetch media types semantics
  const { data: semantics } = useQuery(
    ["media-types-semantics"],
    fetchMediaTypeSemantics,
  )
  // ** patch media label
  const [patch] = useMutation(patchMediaType, {
    onSuccess: (d) => {
      queryCache
        .invalidateQueries([
          "global-media-types",
          { configScope: confScope, configOwnerId },
        ])
        .then(() => {
          setState(d)
        })
    },
  })

  // ** remove media type
  const [remove] = useMutation(deleteMediaType, {
    onSuccess: () => {
      setModalInfo(undefined)
      queryCache.invalidateQueries([
        "global-media-types",
        { configScope: confScope, configOwnerId },
      ])
    },
    onError: () => setModalInfo(undefined),
  })

  // ** create new media type
  const [create] = useMutation(createMediaType, {
    onSuccess: () => {
      queryCache.invalidateQueries([
        "global-media-types",
        { configScope: confScope, configOwnerId },
      ])
      setErrors(undefined)
      onClose()
    },
    // onError: handleError,
    onError: (err: ApiError) => {
      const error = err as ApiError
      const errorMap = error?.errorsMap ?? {}
      setErrors(errorMap)
    },
  })

  const onOpen = (_: any, mediaType?: MediaType, id?: number) => {
    setOpen(() => ({ isOpen: true, id }))
    !!mediaType && setState(mediaType)
  }

  const onClose = () => {
    setOpen(() => ({ isOpen: false, id: undefined }))
    setState(InitialState)
  }

  const handleChange = (
    evt: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = evt.target
    if (name === "semanticCode") {
      setState((s) => ({ ...s, semanticCode: value, name: value }))
      setErrors(undefined)
    } else {
      setState((s) => ({
        ...s,
        [name]: value,
      }))
    }
  }

  const handleColorSelection = (color: string) =>
    setState((s) => ({ ...s, color }))

  const handleColorSelectionEdit = (
    e: ReactMouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    const { style } = e.currentTarget
    setState((s) => ({ ...s, color: rgbToHex(style.color) }))
    patch({ id: state?.id, color: rgbToHex(style.color) })
  }

  const onDelete = (id: string) => {
    setModalInfo({
      title: t("admin.mediaType.delete.title"),
      type: "primary_destructive",
      description: t("admin.mediaType.delete.description"),
      submitLabel: t("admin.mediaType.delete.action"),
      onSubmit: async () => await remove(id),
    })
    onClose()
  }

  const handleCreate = () => {
    const pld = {
      configScope: confScope || "GLOBAL",
      configOwnerId,
      name: state?.name,
      semanticCode: state?.semanticCode,
      color: state?.color,
    }
    create(pld)
  }
  const { hasAuthority } = useAuthenticated()
  const isModalOpen = Boolean(modalInfo)
  const semanticsMapped = semantics?.length
    ? semantics.map((s) => ({ value: s.code, label: s.name }))
    : []

  const globalSurface =
    configScope === "GLOBAL" ||
    configScope === "AGENCY" ||
    configScope === "MARKET"

  return (
    <>
      {globalSurface ? (
        <>
          <Breadcrumbs
            section={t("nav.admin")}
            current={t("nav.admin.media-types")}
          />
          <Header label={t("nav.admin.media-types")}>
            {configScope === "AGENCY" ? <SelectAgency /> : null}
            <WithAuthority allowed={["AUTH_CREATE_MEDIA_TYPE"]}>
              <Button
                variant="contained"
                color="primary"
                onClick={onOpen}
                disabled={open.isOpen === true}
              >
                {t("admin.mediaTypes.create_button")}
              </Button>
            </WithAuthority>
          </Header>
        </>
      ) : (
        <Box display="flex" justifyContent="flex-end">
          <WithAuthority allowed={["AUTH_CREATE_MEDIA_TYPE"]}>
            <Button
              className={styles.button}
              variant="contained"
              color="primary"
              onClick={onOpen}
              disabled={open.isOpen === true}
            >
              {t("admin.mediaTypes.create_button")}
            </Button>
          </WithAuthority>
        </Box>
      )}
      <Surface
        className={cx(styles.surface, {
          [styles.globalSurface]: globalSurface,
        })}
        mt={0}
        mb={2}
        flexDirection="row"
      >
        {open.isOpen && !state?.id ? (
          <div className={cx(styles.container, styles.newContainer)}>
            <div className={styles.inputs}>
              <ColorPicker
                defaultColor={colorOptions[0]}
                options={colorOptions}
                color={colorOptions[0]}
                onSelectedColorChange={handleColorSelection}
              />
              <Options
                name="semanticCode"
                label={t("admin.mediaTypes.semantic")}
                value={state?.semanticCode}
                options={semanticsMapped}
                onChange={handleChange}
                margin="normal"
                size="medium"
                fullWidth={false}
                className={styles.input}
                error={!!errors ? errors["semanticCode"] : ""}
              />
              <TextInput
                name="name"
                label={t("admin.mediaTypes.label")}
                size="medium"
                margin="normal"
                onChange={handleChange}
                value={state?.name}
                className={styles.input}
                error={!!errors && !!errors["name"]}
                errorMessage={!!errors ? errors["name"] : ""}
              />
            </div>
            <div className={styles.buttonGroup}>
              <ButtonGroup>
                <Button variant="outlined" color="primary" onClick={onClose}>
                  {t("modal.label.cancel")}
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  form="new_campaign"
                  onClick={handleCreate}
                  disabled={state.semanticCode === "" || state.name === ""}
                  // disabled={
                  //   status === "loading" ||
                  //   props.isSubmitting ||
                  //   props.dirty === false
                  // }
                >
                  {t("add")}
                </Button>
              </ButtonGroup>
            </div>
          </div>
        ) : (
          <EditMediaTypeDrawer
            open={open}
            onClose={onClose}
            colorOptions={colorOptions}
            mediaType={state}
            data={data}
            handleChange={handleChange}
            handleColorSelection={handleColorSelectionEdit}
            configOwnerId={configOwnerId}
            configScope={confScope}
            patch={patch}
            updatedAt={updatedAt}
          />
        )}

        {loading ? (
          <Loading />
        ) : data?.length ? (
          <WithAuthority allowed={["AUTH_VIEW_MEDIA_TYPE"]}>
            <MediaTypesList
              configScope={confScope}
              data={data}
              selectedId={open?.id}
              onOpen={onOpen}
              onDelete={onDelete}
            />
          </WithAuthority>
        ) : (
          <EmptyState
            icon={<MediaTypeIcon />}
            title={t("admin.mediaTypes.empty.title")}
            subtitle={
              hasAuthority(["AUTH_CREATE_MEDIA_TYPE"])
                ? t("admin.mediaTypes.empty.subtitle")
                : undefined
            }
          >
            {hasAuthority(["AUTH_CREATE_MEDIA_TYPE"]) ? (
              <p
                onClick={onOpen}
                className={cx(
                  "BodyBody-1Primary-mainRegular",
                  styles.createNew,
                )}
              >
                {t("admin.mediaTypes.create_new")}
              </p>
            ) : null}
          </EmptyState>
        )}
      </Surface>
      <ConfirmationModal
        open={isModalOpen}
        onCancel={() => setModalInfo(undefined)}
        {...modalInfo}
      />
    </>
  )
}

export default GlobalMediaTypes

const styles = {
  surface: css`
    padding: 0px;
    margin: 0px;
  `,
  globalSurface: css`
    padding: var(--spacing-xxl-2) var(--spacing-m) var(--spacing-m);
  `,
  wrapper: css`
    padding: 0px;
  `,
  button: css`
    margin: 0px 0px var(--spacing-xxl-2);
  `,
  container: css`
    width: 100%;
    padding: var(--spacing-xl) var(--spacing-m);
    border: 2px solid var(--light-grey);
    margin-bottom: var(--spacing-xs);
    border-radius: 2px;
    display: flex;
    align-items: flex-start;
    height: 108px;
    cursor: pointer;
  `,
  newContainer: css`
    border: 2px dashed var(--light-grey);
    cursor: default;
    justify-content: space-between;
  `,
  inputs: css`
    display: flex;
    align-items: center;
  `,
  input: css`
    width: 200px;
    padding-right: var(--spacing-m);
    &.MuiFormControl-marginNormal {
      margin: 0;
    }
  `,
  buttonGroup: css`
    margin: auto 0;
  `,
  createNew: css`
    padding-top: 1px;
    margin: 0 0.8ch;
    cursor: pointer;
  `,
}
